import React, { useEffect, useState } from 'react';
import PageServices from '../../services/PageServices';
import stockList from '../../stock.json';
import './HotStock.css';

// Hot Stocks: stock futures with a > 2 % move, volume ≥ 6 % above the 10-day average and OI more
// than 15 % above the previous close (GET /hotStocks?date=). BUY = rising, SELL = falling.
// Support / resistance are the previous day's floor pivots.

const REFRESH_MS = 60 * 1000;
const istToday = () => new Date(Date.now() + 330 * 60 * 1000).toISOString().slice(0, 10);
const num = (v) => (Number(v) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const compact = (v) => {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1e7) return `${(n / 1e7).toFixed(2)}Cr`;
  if (Math.abs(n) >= 1e5) return `${(n / 1e5).toFixed(2)}L`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(2)}K`;
  return String(n);
};
const signed = (v) => `${v > 0 ? '+' : ''}${Number(v).toFixed(2)}%`;

// Open a page for a stock in a new tab (same selection as the header search).
const openSymbol = (symbol, url) => {
  const lot = stockList.find((s) => s.name === symbol)?.lotSize;
  localStorage.setItem('stock', symbol);
  if (lot) localStorage.setItem('expiry', lot);
  window.open(url, '_blank', 'noopener,noreferrer');
};
const openChain = (symbol) => openSymbol(symbol, '/optionchain');

// "Historical" (as on Power Stocks): the Backtester for the selected day — at the current minute
// today, at the close (15:30) for a past day.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const historicalUrl = (symbol, date) => {
  const [y, m, d] = date.split('-');
  let hm = '15:30';
  if (date === istToday()) {
    const now = new Date(Date.now() + 330 * 60 * 1000).toISOString().slice(11, 16);
    hm = now < '09:15' ? '09:15' : now > '15:30' ? '15:30' : now;
  }
  return `/backtester?date=${encodeURIComponent(`${d}-${MONTHS[Number(m) - 1]}-${y}`)}&time=${encodeURIComponent(`${hm}:59`)}&symbolToken=${encodeURIComponent(symbol)}`;
};

const Actions = ({ symbol, date }) => (
  <div className="hs-actions">
    <button type="button" className="btn btn-outline-secondary" onClick={() => openSymbol(symbol, historicalUrl(symbol, date))}>Historical</button>
    <button type="button" className="btn btn-primary" onClick={() => openChain(symbol)}>Live</button>
  </div>
);

const StockCard = ({ s, side, date }) => (
  <article className={`hs-card hs-${side}`}>
    <header className="hs-card-head">
      <div>
        <h3>{s.symbol}</h3>
        <span className={`hs-badge hs-badge-${side}`}>{side === 'buy' ? 'BUY' : 'SELL'} · {s.buildup}</span>
      </div>
      <div className="hs-price">
        <strong>₹{num(s.futurePrice)}</strong>
        <span className={s.priceChangePct >= 0 ? 'hs-up' : 'hs-down'}>{signed(s.priceChangePct)}</span>
      </div>
    </header>
    <dl className="hs-metrics">
      <div><dt>Volume vs {s.avgDays}-day avg</dt><dd className="hs-up">{signed(s.volumeAboveAvgPct)}</dd><small>{compact(s.volume)} / {compact(s.avgVolume)}</small></div>
      <div><dt>OI vs prev close</dt><dd className="hs-up">{signed(s.oiChangePct)}</dd><small>{compact(s.oi)} / {compact(s.prevOi)}</small></div>
      <div><dt>Daily trend</dt><dd className={s.trend === 'Bullish' ? 'hs-up' : 'hs-down'}>{s.trend}</dd><small>prev close ₹{num(s.prevClose)}</small></div>
    </dl>
    <div className="hs-levels">
      <div><span>S2</span>{num(s.support[1])}</div>
      <div><span>S1</span>{num(s.support[0])}</div>
      <div className="hs-pivot"><span>Pivot</span>{num(s.pivot)}</div>
      <div><span>R1</span>{num(s.resistance[0])}</div>
      <div><span>R2</span>{num(s.resistance[1])}</div>
    </div>
    <div className="hs-card-foot">
      <button type="button" className="hs-link" onClick={() => openChain(s.symbol)}>Open option chain ↗</button>
      <Actions symbol={s.symbol} date={date} />
    </div>
  </article>
);

const Column = ({ side, items, loading, date }) => (
  <section className="hs-col">
    <h2 className={`hs-col-title hs-${side}-title`}>
      {side === 'buy' ? 'BUY · Rising futures' : 'SELL · Falling futures'} <span>{items.length}</span>
    </h2>
    {side === 'sell' ? (
      // SELL: compact cards, two per row — symbol and trend only
      <div className="hs-mini-grid">
        {items.map((s) => (
          <div key={s.symbol} className="hs-mini hs-sell">
            <strong>{s.symbol}</strong>
            <span className={s.trend === 'Bullish' ? 'hs-up' : 'hs-down'}>{s.trend}</span>
            <Actions symbol={s.symbol} date={date} />
          </div>
        ))}
      </div>
    ) : (
      items.map((s) => <StockCard key={s.symbol} s={s} side={side} date={date} />)
    )}
    {!items.length && !loading && <p className="hs-empty">No {side === 'buy' ? 'BUY' : 'SELL'} stocks meet all three conditions.</p>}
  </section>
);

function HotStock() {
  const [date, setDate] = useState(istToday());
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    const load = (first) => {
      if (first) setStatus('loading');
      PageServices.getHotStocks(date)
        .then((res) => { if (!cancelled) { setResult(res?.data || null); setStatus('ready'); } })
        .catch(() => { if (!cancelled && first) setStatus('error'); });
    };
    load(true);
    const id = date === istToday() ? setInterval(() => load(false), REFRESH_MS) : null;
    return () => { cancelled = true; if (id) clearInterval(id); };
  }, [date]);

  const c = result?.criteria;
  return (
    <div className="hs-page">
      <div className="hs-top">
        <div>
          <h1>Hot Stocks</h1>
          {/* <p className="hs-sub">
            Futures price move &gt; {c?.priceChangePct ?? 2}% · volume ≥ {c?.volumeAboveAvgPct ?? 6}% above {c?.avgDays ?? 10}-day average · OI &gt; {c?.oiChangePct ?? 15}% above previous close · from 09:18
          </p> */}
        </div>
        <label className="hs-date">
          <span>Date</span>
          <input type="date" value={date} max={istToday()} onChange={(e) => setDate(e.target.value)} />
        </label>
      </div>

      {status === 'loading' && <p className="hs-note">Scanning stock futures…</p>}
      {status === 'error' && <p className="hs-note hs-down">Could not load Hot Stocks. Please try again.</p>}
      {status === 'ready' && result?.waiting && <p className="hs-note">Calculation starts at {result.startsAt} IST.</p>}
      {/* {status === 'ready' && !result?.waiting && <p className="hs-note">{result?.scanned || 0} stock futures checked for {result?.date}.</p>} */}

      <div className="hs-grid">
        <Column side="buy" items={result?.buy || []} loading={status === 'loading'} date={date} />
        <Column side="sell" items={result?.sell || []} loading={status === 'loading'} date={date} />
      </div>
    </div>
  );
}

export default HotStock;
